import { labelErrorCorrection } from "./url";

/*
 * The QR code on a label (ADR 0018 › One size for every label): always version 3
 * (29×29 modules) in alphanumeric mode, with the error correction `labelErrorCorrection` picks, so
 * every label looks the same and the layout counts on 29 modules. An encoder of our own
 * (learnings.md, 2026-10-26): libraries choose the version and mode themselves, and the domain
 * stays free of dependencies. It follows ISO/IEC 18004 and Project Nayuki's reference encoder.
 */

/** Modules per side of a version 3 QR code. */
export const QR_SIZE = 29;

const ALPHANUMERIC = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

/** Version 3's data and error correction codewords, by level: one block at M, two at Q. */
const BLOCKS = {
  M: { blocks: 1, data: 44, ec: 26 },
  Q: { blocks: 2, data: 17, ec: 18 },
} as const;

/** The level's two bits in the format information. */
const FORMAT_BITS = { M: 0, Q: 3 } as const;

/** A QR code's modules, `modules[y][x]`, true for dark; without the quiet zone. */
export type QrMatrix = boolean[][];

/**
 * The modules of a label's address. Refuses what `labelErrorCorrection` refuses: lowercase,
 * characters alphanumeric mode can't encode, or more than 61 characters.
 */
export function qrMatrix(url: string): QrMatrix {
  const correction = labelErrorCorrection(url);
  if (!correction.ok) throw new Error(`${correction.error}: ${url}`);
  const level = correction.level;
  const codewords = interleave(encodeData(url, BLOCKS[level].blocks * BLOCKS[level].data), level);

  const modules: QrMatrix = grid(false);
  const reserved: boolean[][] = grid(false);
  const set = (x: number, y: number, dark: boolean) => {
    (modules[y] as boolean[])[x] = dark;
    (reserved[y] as boolean[])[x] = true;
  };
  drawFunctionPatterns(set);
  drawFormatBits(set, level, 0); // reserves the format areas; drawn again with the mask below
  placeData(modules, reserved, codewords);

  let best: { mask: number; penalty: number; modules: QrMatrix } | null = null;
  for (let mask = 0; mask < 8; mask++) {
    const masked = modules.map((row) => [...row]);
    applyMask(masked, reserved, mask);
    drawFormatBits((x, y, dark) => ((masked[y] as boolean[])[x] = dark), level, mask);
    const penalty = penaltyScore(masked);
    if (!best || penalty < best.penalty) best = { mask, penalty, modules: masked };
  }
  return (best as { modules: QrMatrix }).modules;
}

function grid(value: boolean): boolean[][] {
  return Array.from({ length: QR_SIZE }, () => new Array<boolean>(QR_SIZE).fill(value));
}

/** Mode, length and characters as bits, then terminator and padding up to `capacity` bytes. */
function encodeData(text: string, capacity: number): number[] {
  const bits: number[] = [];
  const append = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  append(0b0010, 4); // alphanumeric mode
  append(text.length, 9); // character count, versions 1 to 9
  for (let i = 0; i < text.length; i += 2) {
    const first = ALPHANUMERIC.indexOf(text.charAt(i));
    if (i + 1 < text.length) append(first * 45 + ALPHANUMERIC.indexOf(text.charAt(i + 1)), 11);
    else append(first, 6);
  }
  const capacityBits = capacity * 8;
  append(0, Math.min(4, capacityBits - bits.length));
  append(0, (8 - (bits.length % 8)) % 8);
  const bytes: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    bytes.push(bits.slice(i, i + 8).reduce((byte, bit) => (byte << 1) | bit, 0));
  }
  for (let pad = 0xec; bytes.length < capacity; pad ^= 0xec ^ 0x11) bytes.push(pad);
  return bytes;
}

/** Splits the data into blocks, adds each block's error correction and interleaves them. */
function interleave(data: number[], level: keyof typeof BLOCKS): number[] {
  const { blocks, data: size, ec } = BLOCKS[level];
  const divisor = reedSolomonDivisor(ec);
  const dataBlocks = Array.from({ length: blocks }, (_, b) => data.slice(b * size, (b + 1) * size));
  const ecBlocks = dataBlocks.map((block) => reedSolomonRemainder(block, divisor));
  const result: number[] = [];
  for (let i = 0; i < size; i++) for (const block of dataBlocks) result.push(block[i] as number);
  for (let i = 0; i < ec; i++) for (const block of ecBlocks) result.push(block[i] as number);
  return result;
}

/** Multiplication in GF(2^8) modulo x^8 + x^4 + x^3 + x^2 + 1. */
function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

function reedSolomonDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j] as number, root);
      if (j + 1 < degree) result[j] = (result[j] as number) ^ (result[j + 1] as number);
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function reedSolomonRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const byte of data) {
    const factor = byte ^ (result.shift() as number);
    result.push(0);
    divisor.forEach((coefficient, i) => {
      result[i] = (result[i] as number) ^ gfMultiply(coefficient, factor);
    });
  }
  return result;
}

type SetModule = (x: number, y: number, dark: boolean) => void;

/** Timing patterns, the three finder patterns with their separators, the alignment pattern. */
function drawFunctionPatterns(set: SetModule): void {
  for (let i = 0; i < QR_SIZE; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }
  for (const [cx, cy] of [
    [3, 3],
    [QR_SIZE - 4, 3],
    [3, QR_SIZE - 4],
  ] as const) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= QR_SIZE || y >= QR_SIZE) continue;
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        set(x, y, distance !== 2 && distance !== 4);
      }
    }
  }
  // Version 3 has one alignment pattern, centred at (22, 22).
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++)
      set(22 + dx, 22 + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }
}

/** The level and mask, BCH-protected, in both copies; and the module that is always dark. */
function drawFormatBits(set: SetModule, level: keyof typeof FORMAT_BITS, mask: number): void {
  const data = (FORMAT_BITS[level] << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i++) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  const bits = ((data << 10) | remainder) ^ 0x5412;
  const bit = (i: number) => ((bits >>> i) & 1) !== 0;
  for (let i = 0; i <= 5; i++) set(8, i, bit(i));
  set(8, 7, bit(6));
  set(8, 8, bit(7));
  set(7, 8, bit(8));
  for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i));
  for (let i = 0; i < 8; i++) set(QR_SIZE - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) set(8, QR_SIZE - 15 + i, bit(i));
  set(8, QR_SIZE - 8, true);
}

/** The codewords in the zigzag from the bottom right, two columns at a time, around column 6. */
function placeData(modules: QrMatrix, reserved: boolean[][], codewords: readonly number[]): void {
  let i = 0;
  for (let right = QR_SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < QR_SIZE; vertical++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? QR_SIZE - 1 - vertical : vertical;
        if ((reserved[y] as boolean[])[x] || i >= codewords.length * 8) continue;
        (modules[y] as boolean[])[x] =
          (((codewords[i >>> 3] as number) >>> (7 - (i & 7))) & 1) !== 0;
        i++;
      }
    }
  }
}

const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function applyMask(modules: QrMatrix, reserved: boolean[][], mask: number): void {
  const invert = MASKS[mask] as (x: number, y: number) => boolean;
  for (let y = 0; y < QR_SIZE; y++) {
    for (let x = 0; x < QR_SIZE; x++) {
      if (!(reserved[y] as boolean[])[x] && invert(x, y)) {
        const row = modules[y] as boolean[];
        row[x] = !row[x];
      }
    }
  }
}

/** The four penalty rules of ISO/IEC 18004 § 7.8.3: the mask with the lowest wins. */
function penaltyScore(modules: QrMatrix): number {
  const at = (x: number, y: number) => (modules[y] as boolean[])[x] as boolean;
  const lines: boolean[][] = [];
  for (let i = 0; i < QR_SIZE; i++) {
    lines.push(modules[i] as boolean[]);
    lines.push(Array.from({ length: QR_SIZE }, (_, j) => at(i, j)));
  }
  let score = 0;
  const finderLike = /10111010000|00001011101/g;
  for (const line of lines) {
    let run = 1;
    for (let i = 1; i <= line.length; i++) {
      if (i < line.length && line[i] === line[i - 1]) {
        run++;
        continue;
      }
      if (run >= 5) score += 3 + (run - 5);
      run = 1;
    }
    score +=
      (line
        .map((dark) => (dark ? "1" : "0"))
        .join("")
        .match(finderLike)?.length ?? 0) * 40;
  }
  let dark = 0;
  for (let y = 0; y < QR_SIZE; y++) {
    for (let x = 0; x < QR_SIZE; x++) {
      if (at(x, y)) dark++;
      if (x < QR_SIZE - 1 && y < QR_SIZE - 1) {
        const color = at(x, y);
        if (at(x + 1, y) === color && at(x, y + 1) === color && at(x + 1, y + 1) === color) {
          score += 3;
        }
      }
    }
  }
  score += Math.floor(Math.abs((dark * 100) / (QR_SIZE * QR_SIZE) - 50) / 5) * 10;
  return score;
}
